import { FC, useMemo } from "react"
import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import Icon from "@mui/material/Icon"
import Tooltip from "@mui/material/Tooltip" // ツールチップを綺麗にするため追加

export interface OnRatioDataPoint {
  start_time: number
  end_time: number
  on_ratio: number
  offline: boolean
}

interface HourlyOnRatioGraphProps {
  onRatios: OnRatioDataPoint[]
  initialized: number
}

const getLastNDays = (n: number): Date[] => {
  const days: Date[] = []
  for (let i = n - 1; i >= 0; i--) {
    const date = new Date()
    date.setHours(0, 0, 0, 0)
    date.setDate(date.getDate() - i)
    days.push(date)
  }
  return days
}

const formatDate = (date: Date): string => {
  const daysOfWeek = ["日", "月", "火", "水", "木", "金", "土"]
  return `${date.getMonth() + 1}/${date.getDate()} (${daysOfWeek[date.getDay()]})`
}

const getColorFromRatio = (ratio: number): string => {
  if (ratio === 0) return "#FD9A14"
  if (ratio < 0.1) return "#FDBE85"
  if (ratio < 0.2) return "#FCE5CD"
  if (ratio < 0.3) return "#FDF3EA"
  return "#fff"
}

export const HourlyOnRatioGraph: FC<HourlyOnRatioGraphProps> = ({ onRatios, initialized }) => {
  const days = useMemo(() => getLastNDays(7), [])

  const rows = useMemo(() => {
    const result: { hour: number; values: (OnRatioDataPoint | null)[] }[] = []
    for (let hour = 0; hour < 24; hour++) {
      const row: (OnRatioDataPoint | null)[] = []
      for (let day = 0; day < 7; day++) {
        const slotStart = new Date(days[day])
        slotStart.setHours(hour, 0, 0, 0)
        const slotUnix = Math.floor(slotStart.getTime() / 1000)
        const matched = onRatios.find((item) => item.start_time === slotUnix)
        row.push(matched || null)
      }
      result.push({ hour, values: row })
    }
    return result
  }, [onRatios, days])

  // 太線にする時間の判定 (0, 6, 12, 18, 24)
  const isBoldLine = (hour: number) => hour % 6 === 0

  return (
    <Card>
      <MDBox pt={3} px={3}>
        <MDTypography variant="h4" fontWeight="medium">
          不在検知/機器エラーグラフ
        </MDTypography>
        <MDBox display="flex" alignItems="center" mt={1} gap={2}>
          <MDBox display="flex" alignItems="center" sx={{ fontSize: "0.875rem" }} gap={0.5}>
            <Icon sx={{ color: "#f44336", fontSize: "1.2rem !important" }}>warning</Icon>
            <MDTypography variant="button" color="text">
              機器エラー
            </MDTypography>
          </MDBox>
          <MDBox display="flex" alignItems="center" sx={{ fontSize: "0.875rem" }} gap={0.5}>
            <div style={{ width: "12px", height: "12px", backgroundColor: "#FD9A14", borderRadius: "2px" }}></div>
            <MDTypography variant="button" color="text">
              不在検知
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>

      <MDBox p={3} pt={4}>
        {initialized === 1 && (
          <div style={{ width: "100%", textAlign: "center" }}>
            <div className="spinner" style={{ borderTopColor: "#bbbbbb" }}></div>
          </div>
        )}
        {initialized === 2 && (
          <div>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th style={{ width: "50px" }} />
                  {days.map((day, idx) => {
                    const dayOfWeek = day.getDay()
                    const color = dayOfWeek === 0 ? "#FF0000" : dayOfWeek === 6 ? "#0000FF" : "#000"
                    return (
                      <th key={idx} style={{ paddingBottom: "10px", textAlign: "center" }}>
                        <MDTypography variant="caption" sx={{ color, fontWeight: "bold" }}>
                          {formatDate(day)}
                        </MDTypography>
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const isBold = isBoldLine(row.hour)
                  const showText = row.hour % 3 === 0

                  return (
                    <tr key={row.hour} style={{ height: "14px" }}>
                      <td style={{ position: "relative", textAlign: "right", paddingRight: "8px" }}>
                        {showText && (
                          <div
                            style={{
                              position: "absolute",
                              top: "-20px", // 罫線の真横にくるように調整
                              right: "8px",
                              width: "100%"
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              sx={{
                                fontSize: "10px",
                                fontWeight: isBold ? "bold" : "normal",
                                color: isBold ? "#000" : "#777"
                              }}
                            >
                              {`${row.hour}:00`}
                            </MDTypography>
                          </div>
                        )}
                      </td>
                      {row.values.map((cell, idx) => (
                        <Tooltip
                          key={idx}
                          title={
                            cell
                              ? `${formatDate(days[idx])} ${row.hour}:00～${row.hour + 1}:00\n検知率: ${(
                                  cell.on_ratio * 100
                                ).toFixed(1)}%`
                              : "データなし"
                          }
                          arrow
                          placement="top"
                          componentsProps={{
                            tooltip: { sx: { whiteSpace: "pre-line", fontSize: "11px" } }
                          }}
                        >
                          <td
                            style={{
                              backgroundColor: cell ? getColorFromRatio(cell.on_ratio) : "#f5f5f5",
                              borderTop: isBold ? "2px solid #555" : "1px solid #ddd",
                              borderLeft: "1px solid #ddd",
                              borderRight: "1px solid #ddd",
                              padding: 0,
                              cursor: "pointer",
                              textAlign: "center"
                            }}
                          >
                            {cell?.offline && (
                              <Icon
                                sx={{
                                  color: "#f44336",
                                  fontSize: "12px !important",
                                  display: "block",
                                  margin: "0 auto"
                                }}
                              >
                                warning
                              </Icon>
                            )}
                          </td>
                        </Tooltip>
                      ))}
                    </tr>
                  )
                })}
                {/* 24:00 のラベルを表示するための空行 */}
                <tr style={{ height: "0px" }}>
                  <td style={{ position: "relative", textAlign: "right", paddingRight: "8px" }}>
                    <div style={{ position: "absolute", top: "-20px", right: "8px", width: "100%" }}>
                      <MDTypography variant="caption" sx={{ fontSize: "10px", fontWeight: "bold", color: "#000" }}>
                        24:00
                      </MDTypography>
                    </div>
                  </td>
                  {days.map((_, idx) => (
                    <td key={idx} style={{ borderTop: "2px solid #555" }} />
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </MDBox>
    </Card>
  )
}

export default HourlyOnRatioGraph
